import Constansts from "../components.constants.js";
import Utils from "../components.utils.js";
import axios from "axios";

const CancelToken = axios.CancelToken;
const fileUploadXHR = {};

class DispatcherEvent {
    constructor(name) {
        this.name = name;
        this.callbacks = [];
    }
    registerCallback(cb) { this.callbacks.push(cb); }
    unregisterCallback(cb) {
        const i = this.callbacks.indexOf(cb);
        if (i > -1) this.callbacks.splice(i, 1);
    }
    fire(data) {
        this.callbacks.slice().forEach(cb => cb(data));
    }
}

class FileUploaderToCloud {

    constructor(
        file,
        jobResponseForUploadUrl,
        uploadResponse,
        filesUploadDataFromResponse,
        createdTime,
        finalisedFileName,
        start = true,
        previousSessionInfo = null, 
        groupId = null, 
        isFolderUpload = false
    ) {
        this.file = file;
        this.fileSize = file.size;
        this.fileName = finalisedFileName;

        this.responseDataOfAws = jobResponseForUploadUrl.fields;
        this.awsUploadUrl = jobResponseForUploadUrl.url;

        this.binId = uploadResponse.binId;
        this.jobId = filesUploadDataFromResponse.job_id;
        this.groupId = groupId;
        this.isFolderUpload = isFolderUpload;
        this.fileUID = filesUploadDataFromResponse.data.file_uid;
        this.fileUid = filesUploadDataFromResponse.data.file_uid;
        this.uploadToken = filesUploadDataFromResponse.data.upload_token;
        this.parentPath = filesUploadDataFromResponse.data.parent_path;
        this.cloudType = filesUploadDataFromResponse.data.cloud_type;

        const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

        this.chunkSize = Math.min(
            Utils.getMaxChunkSizeForCloud(),
            Math.max(Math.floor(this.fileSize / 2), MIN_CHUNK_SIZE)
        );
        if (previousSessionInfo?.session_info) {
            this.collectChunkUploadUrl = previousSessionInfo.session_info;
        } else {
            this.collectChunkUploadUrl = null;
        }

        this.events = {};
        this.status = "INITIALIZING";
        this.uploadPercentage = 0;
        this.totalUploaded = 0;

        this.isUploadPaused = false;
        this.isUploadAborted = false;

        this.bandwidth = 0;
        this.createdTime = createdTime;
        this.proxy_info = { "thumbnail_uri": "" };

        /** 🔒 CRITICAL STATE */
        this.partState = new Map();              // partNo → PENDING | UPLOADING | COMPLETED
        this.inFlightRequests = new Map();       // partNo → cancelTokenSource
        this.completedParts = [];                // AWS completion payload

        this.previousSessionInfo = previousSessionInfo;
        this.startTime = null;

        if (start) this.start();
    }

    /* ---------------------------------------------------- */
    /* Lifecycle                                            */
    /* ---------------------------------------------------- */

    start() {
        this.startTime = performance.now();
        const totalParts = Math.ceil(this.fileSize / this.chunkSize);

        if (this.fileSize <= Utils.getMaxChunkSizeForCloud()) {
            if (this.cloudType == "vayucloud"){
                return this.singlePartUploadToVayu();
            } else {
                return this.singlePartUpload();
            }
        }

        this.initPartState(totalParts);
        return this.multipartUpload(totalParts);
    }

    pause() {
        this.isUploadPaused = true;

        this.inFlightRequests.forEach((source, partNo) => {
            source.cancel("Paused by user");
            this.partState.set(partNo, "PENDING");
        });

        this.inFlightRequests.clear();
    }

    resume() {
        if (!this.isUploadPaused) return;
        this.isUploadPaused = false;
        const totalParts = Math.ceil(this.fileSize / this.chunkSize);
        return this.multipartUpload(totalParts);
    }

    /* ---------------------------------------------------- */
    /* Part State Machine                                   */
    /* ---------------------------------------------------- */

    initPartState(totalParts) {
        for (let i = 1; i <= totalParts; i++) {
            if (!this.partState.has(i)) {
                this.partState.set(i, "PENDING");
            }
        }

        if (this.previousSessionInfo?.previousSessionChunks?.length) {
            this.previousSessionInfo.previousSessionChunks.forEach(p => {
                this.partState.set(p.PartNumber, "COMPLETED");
                this.completedParts.push(p);
                this.totalUploaded += this.chunkSize;
            });
        }
    }

    reserveNextPart(start, step, totalParts) {
        for (let i = start; i <= totalParts; i += step) {
            if (this.partState.get(i) === "PENDING") {
                this.partState.set(i, "UPLOADING");
                return i;
            }
        }
        return null;
    }

    /* ---------------------------------------------------- */
    /* Multipart Upload                                     */
    /* ---------------------------------------------------- */

    async multipartUpload(totalParts) {
        try {
            if (!this.collectChunkUploadUrl) {
                const payload = {
                    bin_id: this.binId,
                    object_key: encodeURIComponent(this.responseDataOfAws.key),
                    job_id: this.jobId,
                    size: this.fileSize,
                    file_name: this.file.name,
                    dest_dir: this.parentPath,
                    is_folder_upload: this.isFolderUpload,
                    relative_path: this.isFolderUpload ? this.file.webkitRelativePath : undefined
                };

                const { data } = await axios.post(
                    `${Constansts.uploadUrl}/multipart/initiate-upload`,
                    payload,
                    {
                        cancelToken: new CancelToken(c => {
                            fileUploadXHR[this.fileUid] = c;
                        })
                    }
                );

                this.collectChunkUploadUrl = data;
            }

            this.status = "INPROGRESS";
            this.dispatch("initializing", this.getProgressInfo());

            const workers = [];
            for (let i = 1; i <= Constansts.maxUploadThreads; i++) {
                workers.push(this.uploadWorker(i, totalParts));
            }

            await Promise.all(workers);

            if (!this.isUploadPaused) {
                await this.completeMultipartUpload();
            }
        } catch (err) {
            this.fail(err);
        }
    }

    async uploadWorker(startIndex, totalParts) {
        while (!this.isUploadPaused && !this.isUploadAborted) {
            const partNo = this.reserveNextPart(
                startIndex,
                Constansts.maxUploadThreads,
                totalParts
            );

            if (!partNo) return;

            const start = (partNo - 1) * this.chunkSize;
            const end = Math.min(start + this.chunkSize, this.fileSize);
            const blob = this.file.slice(start, end);

            try {
                const uploadUrl = await this.fetchChunkUploadUrl(partNo);
                const result = await this.uploadPart(uploadUrl, blob, partNo);

                this.completedParts.push(result);
                this.partState.set(partNo, "COMPLETED");

                this.totalUploaded += (end - start);
                this.updateProgress();
            } catch (e) {
                if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || this.isUploadPaused) {
                    this.partState.set(partNo, "PENDING");
                    return;
                }
                this.partState.set(partNo, "PENDING");
                throw e;
            }
        }
    }

    async uploadPart(uploadUrl, blob, partNo) {
        const source = CancelToken.source();
        this.inFlightRequests.set(partNo, source);

        const resp = await axios.put(uploadUrl, blob, {
            cancelToken: source.token,
            headers: this.cloudType === "vayucloud" ? {
                "Content-Type": "application/octet-stream",
            } : {}
        });

        this.inFlightRequests.delete(partNo);

        return {
            ETag: resp.headers.etag,
            PartNumber: partNo
        };
    }

    async abortPost(payload) {
        this.isUploadAborted = true;
        // source.cancel();
        try {
            await axios.delete(`${Constansts.uploadUrl}/multipart/${payload.job_id}/file/${payload.file_uid}`, payload);
            fileUploadXHR[payload.file_uid]();
            this.status = "FAILED";
            this.dispatch("exit", this.getProgressInfo());
        } catch (error) {
            if (error.response) {
                error.response.data.statusCode = error.response.status
                return error.response.data
            }
            return {}
        }
    }

    /* ---------------------------------------------------- */
    /* Completion                                           */
    /* ---------------------------------------------------- */

    async completeMultipartUpload() {
        this.completedParts.sort((a, b) => a.PartNumber - b.PartNumber);

        await axios.post(`${Constansts.uploadUrl}/multipart/complete-upload`, {
            upload_id: this.collectChunkUploadUrl.upload_id,
            object_key: this.collectChunkUploadUrl.object_key,
            bucket_name: this.collectChunkUploadUrl.bucket_name,
            parts: this.completedParts
        });

        await axios.post(`${Constansts.uploadUrl}/cloud`, "", {
            headers: {
                "File-Size": this.file.size,
                "Job-Id": this.jobId,
                "Token-Id": this.uploadToken,
                "File-Uid": this.fileUid,
                "File-Name": encodeURIComponent(this.fileName),
                "Slug-Name": encodeURIComponent(this.file.name),
                "Dst-Dir": this.parentPath,
                "Last-Modified": this.file.lastModified,
                "Relative-Path": this.isFolderUpload ? encodeURIComponent(this.file.webkitRelativePath) : undefined,
            }
        });

        this.status = "COMPLETED";
        this.uploadPercentage = 100;

        this.dispatch("complete", this.getProgressInfo());
        this.dispatch("fileUploadDone", this.getProgressInfo());
        this.dispatch("exit", this.getProgressInfo());
    }
    /*---------------------------------------------------- */
    /* Single Part Upload                                  */
    /* ---------------------------------------------------- */
    humanizeBandwidth(bytes) {
        if (!bytes || bytes == 0) {
            return { bandwidth: 0, unit: "KB/s" };
        }
        if (bytes < 0) {
            return { bandwidth: 0, unit: "KB/s" };
        }
        if (bytes < 1024) return { bandwidth: bytes.toFixed(2), unit: " B/s" };
        else if (bytes < 1048576)
            return { bandwidth: (bytes / 1024).toFixed(2), unit: "KB/s" };
        else if (bytes < 1073741824)
            return { bandwidth: (bytes / 1048576).toFixed(2), unit: "MB/s" };
        else if (bytes < 1099511627776)
            return { bandwidth: (bytes / 1073741824).toFixed(2), unit: "GB/s" };
        else return { bandwidth: (bytes / 1099511627776).toFixed(2), unit: "TB/s" };
    }

    async singlePartUpload() {
        try {
            this.uploadPercentage = 0;
            // this.status = "INITIALIZING";
            // this.dispatch("initializing", this.getProgressInfo());
            let formData = new FormData();
            formData.append("key", this.responseDataOfAws.key);
            formData.append("policy", this.responseDataOfAws.policy);
            formData.append("x-amz-algorithm", this.responseDataOfAws["x-amz-algorithm"]);
            formData.append("x-amz-credential", this.responseDataOfAws["x-amz-credential"]);
            formData.append("x-amz-date", this.responseDataOfAws["x-amz-date"]);
            formData.append("x-amz-signature", this.responseDataOfAws["x-amz-signature"]);
            formData.append("file", this.file);
            axios.post(this.awsUploadUrl, formData, {
                cancelToken: new CancelToken(c => {
                    fileUploadXHR[this.fileUid] = c;
                })
            }).then(() => {
                let payload = {};
                payload.params = {
                    "File-Size": this.file.size,
                    "Job-Id": this.jobId,
                    "Token-Id": this.uploadToken,
                    "File-Uid": this.fileUid,
                    "File-Name": encodeURIComponent(this.fileName),
                    "Slug-Name": encodeURIComponent(this.file.name),
                    "Dst-Dir": this.parentPath,
                    "Last-Modified": this.file.lastModified,
                    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0;) Gecko/20100101 Firefox/68.0',
                    "Relative-Path": this.isFolderUpload ? encodeURIComponent(this.file.webkitRelativePath) : undefined,
                };
                axios.post(`${Constansts.uploadUrl}/cloud`, "", { headers: payload.params }).then(() => {
                    console.log("[AWS singlePartUpload ]");
                    this.uploadPercentage = 100;
                    this.dispatch("progress", this.getProgressInfo());
                    this.status = "COMPLETED";
                    this.dispatch("complete", this.getProgressInfo());
                    this.dispatch("exit", this.getProgressInfo());
                    this.dispatch("fileUploadDone", this.getProgressInfo());
                });
            }, (error) => {
                console.log("error", error)
                this.uploadPercentage = -1;
                this.status = "FAILED";
                this.dispatch("exit", this.getProgressInfo());
            });
        } catch (error) {
            this.uploadPercentage = -1;
            this.status = "FAILED";
            this.dispatch("exit", this.getProgressInfo());
            if (error.response) {
                error.response.data.statusCode = error.response.status
                return error.response.data
            }
            return {}
        }
    }

    async singlePartUploadToVayu() {
        try {
            this.uploadPercentage = 0;
            // this.status = "INITIALIZING";
            // this.dispatch("initializing", this.getProgressInfo());
            let params = {
                // "response-content-disposition": encodeURIComponent(`attachment; filename="${this.file.name}"`),
                "X-Amz-Algorithm": this.responseDataOfAws["x-amz-algorithm"],
                "X-Amz-Credential": this.responseDataOfAws["x-amz-credential"],
                "X-Amz-Date": this.responseDataOfAws["x-amz-date"],
                "X-Amz-SignedHeaders": this.responseDataOfAws["x-amz-signedheaders"]?? "host",
                "X-Amz-Expires": this.responseDataOfAws["x-amz-expires"]?? 3600,
                "X-Amz-Signature": this.responseDataOfAws["x-amz-signature"],
            }
            axios.put(this.awsUploadUrl+'/'+this.responseDataOfAws['key'], this.file, {
                params: params,
                headers: { 
                    'Content-Type': 'application/octet-stream',
                    // 'Content-Length': this.file.size
                },
                cancelToken: new CancelToken(c => {
                    fileUploadXHR[this.fileUid] = c;
                })
            }).then(() => {
                let payload = {};
                payload.params = {
                    "File-Size": this.file.size,
                    "Job-Id": this.jobId,
                    "Token-Id": this.uploadToken,
                    "File-Uid": this.fileUid,
                    "File-Name": encodeURIComponent(this.fileName),
                    "Slug-Name": encodeURIComponent(this.file.name),
                    "Dst-Dir": this.parentPath,
                    "Last-Modified": this.file.lastModified,
                    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0;) Gecko/20100101 Firefox/68.0',
                    "Relative-Path": this.isFolderUpload ? encodeURIComponent(this.file.webkitRelativePath) : undefined,
                };
                axios.post(`${Constansts.uploadUrl}/cloud`, "", { headers: payload.params }).then(() => {
                    console.log("[AWS singlePartUpload ]");
                    this.uploadPercentage = 100;
                    this.dispatch("progress", this.getProgressInfo());
                    this.status = "COMPLETED";
                    this.dispatch("complete", this.getProgressInfo());
                    this.dispatch("exit", this.getProgressInfo());
                    this.dispatch("fileUploadDone", this.getProgressInfo());
                });
            }, (error) => {
                console.log("error", error)
                this.uploadPercentage = -1;
                this.status = "FAILED";
                this.dispatch("exit", this.getProgressInfo());
            });
        } catch (error) {
            this.uploadPercentage = -1;
            this.status = "FAILED";
            this.dispatch("exit", this.getProgressInfo());
            if (error.response) {
                error.response.data.statusCode = error.response.status
                return error.response.data
            }
            return {}
        }
    }

    /* ---------------------------------------------------- */
    /* Helpers                                              */
    /* ---------------------------------------------------- */

    async fetchChunkUploadUrl(partNo) {
        const u = this.collectChunkUploadUrl;
        const resp = await axios.get(
            `${Constansts.uploadUrl}/multipart/chunk-url` +
            `?object_key=${encodeURIComponent(u.object_key)}` +
            `&upload_id=${u.upload_id}` +
            `&bucket_name=${u.bucket_name}` +
            `&part_number=${partNo}`
        );
        return resp.data.upload_url;
    }

    updateProgress() {
        this.uploadPercentage = Math.min(
            (this.totalUploaded / this.fileSize) * 100,
            100
        );
        this.dispatch("progress", this.getProgressInfo());
    }

    getProgressInfo() {
        return {
            uploadedPercentage: Math.round(this.uploadPercentage),
            uploaded: this.totalUploaded,
            fileUID: this.fileUid,
            status: this.status,
            jobId: this.jobId,
            fileInfo: this.file
        };
    }

    fail(err) {
        this.status = "FAILED";
        this.dispatch("exit", this.getProgressInfo());
        throw err;
    }

    on(name, cb) {
        if (!this.events[name]) {
            this.events[name] = new DispatcherEvent(name);
        }
        this.events[name].registerCallback(cb);
    }

    dispatch(name, data) {
        this.events[name]?.fire(data);
    }
}

export default FileUploaderToCloud;
